import { gql } from '@apollo/client';

/* ── investment proposals ───────────────────────────────────── */

/**
 * The client's saved proposals, newest first — all of them, or one goal's.
 *
 * A proposal is a recommendation someone chose to KEEP: until
 * `saveInvestmentProposal` is called a recommendation is working state held
 * for 24 hours, and only then is it listed here. The backend records no
 * lifecycle beyond that — no draft, active or executed column exists — and
 * `source` is the engine that built the plan (subagent | rebalance | stp |
 * resize), decided at save time from the plan's own contents.
 *
 * `limit` and `offset` are NON-NULL on the resolver despite having defaults
 * there, so they are always sent.
 *
 * Resolves the user from the token: an advisor reading a client's proposals
 * sends the client through `asClient` (X-Target-User-Id).
 */
export const LIST_INVESTMENT_PROPOSALS = gql`
  query ListInvestmentProposals($goalId: Int, $limit: Int!, $offset: Int!) {
    listInvestmentProposals(goalId: $goalId, limit: $limit, offset: $offset) {
      success
      message
      count
      proposals {
        urlId
        source
        title
        summary
        notes
        actionType
        subjectSymbol
        subjectName
        subjectAssetType
        candidateSymbols
        goalId
        portfolioSymbol
        investmentAccountId
        runInputs
        createdAt
      }
    }
  }
`;

/* One analysis panel: the working behind a verdict in a single discipline.
   Every panel shares this shape, so it is spelled once. */
const PANEL_FIELDS = `
  signal
  colorCode
  view
  summary
  metrics { param value benchmark status }
  headlines { date sentiment title }
`;

/* The recommendation payload, mirrored from the consumer app's own selection
   so both surfaces render the same fields. Validated against the live schema. */
const RECOMMENDATION_DATA_FIELDS = `
  plans {
    agentLean
    agentLeanRationale
    anchorSymbol
    task
    candidates {
      action
      assetType
      modelId
      variantId
      portfolioType
      category
      cons
      name
      overallScore
      portfolioImpactNote
      pros
      quantityContext { note range units value }
      symbol
      thesis {
        verdict
        thesisVerdict
        colorCode
        confidence
        summary
        horizonFit
        focusAreas
        monitoring
        supporting
        contradicting
        redFlags
        thesisInvalidators
        triggeredInvalidators
        originalThesisStatus
        originalThesisDelta
        researchRecommendation
        researchReportUrl
        perQuestion { question answer view passed score colorCode source panelRefs redFlags }
        tradeThesisDraft {
          rationale catalysts risks entryTrigger targetPriceOrReturn
          exitTradeConditions portfolioPurpose monitoringFrequency
        }
      }
      panels {
        detailPanels {
          mfQuality { ${PANEL_FIELDS} }
          etfQuality { ${PANEL_FIELDS} }
          indexMethodology { ${PANEL_FIELDS} }
          fundamentals { ${PANEL_FIELDS} }
          valuation { ${PANEL_FIELDS} }
          technicals { ${PANEL_FIELDS} }
          qualitative { ${PANEL_FIELDS} }
          news { ${PANEL_FIELDS} }
          modelFit { ${PANEL_FIELDS} }
        }
        portfolioFit { ${PANEL_FIELDS} }
        taxImpact { ${PANEL_FIELDS} }
        sellJustification { ${PANEL_FIELDS} }
      }
    }
    comparisonTable { metric values label }
    exposures { sector weightPct }
    exposuresMessage
    planLabel
    planPanels
    planTitle
    scope { goalId goalName portfolioName scopeSummary }
    currentAssetClasses { assetClass weightPct }
    assetClassesNew { assetClass weightPct }
    exposuresNew { sector weightPct }
    portfolioImpact {
      headline
      verdict
      metrics { metric label current proforma change isImprovement }
      assetClassChanges { name currentWeight newWeight change }
      sectorChanges { name currentWeight newWeight change }
      industryChanges { name currentWeight newWeight change }
      factorChanges { name currentWeight newWeight change }
      flags { code message severity }
    }
    executionPlan {
      recurring
      executionLegs {
        side phase fundName fundHouse holdingSecuritySymbol tradeValue quantity price
        settlementDate settlementType reason skipped skipReason switchWith
        currentWeight targetWeight weightChange
      }
    }
  }
  summary
  comparisonTable { metric values label }
`;

/**
 * One recommendation's plan — a saved proposal, or a run not yet saved.
 *
 * Served from the proposal row once saved, so it still opens after the 24
 * hour cache has let the working copy go.
 */
export const INVESTMENT_RECOMMENDATION = gql`
  query InvestmentRecommendation($urlId: String!) {
    getInvestmentRecommendation(urlId: $urlId) {
      success
      message
      urlId
      jobStatus
      data { ${RECOMMENDATION_DATA_FIELDS} }
    }
  }
`;

/**
 * Run any recommendation analysis and publish it under a urlId.
 *
 * A QUERY on the schema, though it starts work. It is asynchronous by default:
 * the first call returns a jobId with jobStatus "running", and the result is
 * collected by calling it again with that jobId — nothing re-runs, the job's
 * state comes back (running | done | failed | unknown), and on "done" the
 * urlId is the published recommendation. Because the same variables must keep
 * returning fresh answers, it is never read from the Apollo cache.
 */
/*
 * `actionType` is the enum the field takes, not a string. Declared as `String!`
 * the whole document is rejected — "used in position expecting type
 * AnalysisActionType!" — and Run analysis fails before it reaches the server.
 * The values the screen already sends are the enum's own (rebalance_portfolio,
 * cash_invest, swap_holding and the rest), so only the declaration was wrong.
 */
export const RUN_INVESTMENT_ANALYSIS = gql`
  query RunInvestmentAnalysis(
    $actionType: AnalysisActionType!
    $goalId: Int!
    $portfolioId: Int
    $proposedTrades: [ProposedTradeInput!]
    $amount: Float
    $horizon: String
    $rationale: String
    $otherNotes: String
    $securityListUrlId: String
    $recurring: Boolean
    $fundCount: Int
    $asyncMode: Boolean
    $jobId: String
  ) {
    runInvestmentAnalysis(
      actionType: $actionType
      goalId: $goalId
      portfolioId: $portfolioId
      proposedTrades: $proposedTrades
      amount: $amount
      horizon: $horizon
      rationale: $rationale
      otherNotes: $otherNotes
      securityListUrlId: $securityListUrlId
      recurring: $recurring
      fundCount: $fundCount
      asyncMode: $asyncMode
      jobId: $jobId
    ) {
      success
      message
      urlId
      jobId
      jobStatus
    }
  }
`;

/**
 * The signed-in user's active trade theses — what a fund manager has finalised.
 *
 * Per user, not per fund: the backend keys a thesis by user and security, so a
 * name finalised for one fund is the same record read from another. The
 * finalised list therefore shows every active thesis, with the fund each was
 * written for carried in `portfolioPurpose`.
 */
export const ALL_TRADE_THESES = gql`
  query AllTradeTheses {
    getAllTheses {
      thesisId
      securitySymbol
      assetType
      horizon
      status
      decision
      rationale
      portfolioPurpose
      createdAt
      nextReviewAt
    }
  }
`;
